import { motion } from 'framer-motion'
import { Brain, Lightbulb, TrendingUp, Wand2 } from 'lucide-react'

import { useCommandDashboard } from '@/contexts/CommandDashboardContext'

export function AiSalonAssistant() {
  const { data } = useCommandDashboard()
  const intelligence = data?.intelligence ?? {}
  const forecast = data?.revenue_forecast ?? {}

  const performance = Array.isArray(intelligence.performance_summary)
    ? intelligence.performance_summary
    : []
  const recommendations = Array.isArray(intelligence.recommendations)
    ? intelligence.recommendations
    : []
  const predicted = Array.isArray(intelligence.predicted_opportunities)
    ? intelligence.predicted_opportunities.map((row) => ({
        label: row.amount
          ? `+AED ${row.amount} — ${row.label ?? 'opportunity'}`
          : (row.label ?? ''),
        probability: row.probability ?? null,
      }))
    : []

  const likelihood = Number(forecast.likelihood_pct ?? 0)

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-intelligence relative overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(262_83%_58%/0.08),transparent_60%)]" />
      <div className="pointer-events-none absolute -left-12 bottom-0 h-32 w-32 rounded-full bg-gold/8 blur-3xl" />

      <div className="relative px-5 py-4">
        <div className="flex items-center gap-3.5">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-[hsl(262_70%_48%)] text-white shadow-glow">
            <Wand2 className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-bold tracking-tight">AI Salon Intelligence</h2>
              <span className="rounded-full bg-primary px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground shadow-sm">
                Live
              </span>
            </div>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Performance · recommendations · revenue opportunities
            </p>
          </div>
        </div>
      </div>

      <div className="relative grid lg:grid-cols-3">
        <div className="px-5 pb-5 pt-1 lg:pr-4">
          <div className="mb-2.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">
            <TrendingUp className="h-3.5 w-3.5" />
            Performance summary
          </div>
          <ul className="space-y-2.5 text-[12px] leading-relaxed text-foreground/90">
            {performance.length > 0 ? performance.map((line) => (
              <li key={line}>{line}</li>
            )) : (
              <li className="text-muted-foreground">No performance insights yet today.</li>
            )}
          </ul>
        </div>

        <div className="border-t border-primary/[0.06] px-5 py-4 lg:border-l lg:border-t-0">
          <div className="mb-2.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">
            <Lightbulb className="h-3.5 w-3.5" />
            Recommendations
          </div>
          <ul className="space-y-2.5 text-[12px] leading-relaxed text-foreground/90">
            {recommendations.length > 0 ? recommendations.map((line) => (
              <li key={line}>{line}</li>
            )) : (
              <li className="text-muted-foreground">No recommendations right now.</li>
            )}
          </ul>
        </div>

        <div className="border-t border-primary/[0.06] bg-primary/[0.03] px-5 py-4 lg:border-l lg:border-t-0">
          <div className="mb-2.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gold">
            <Brain className="h-3.5 w-3.5" />
            Predicted opportunities
          </div>
          <ul className="space-y-2.5 text-[12px] leading-relaxed">
            {predicted.length > 0 ? predicted.map((row) => (
              <li key={row.label}>
                {row.probability != null ? (
                  <>
                    <span className="font-bold text-emerald-600">{row.probability}% probability</span>
                    <span className="text-muted-foreground"> {row.label.replace(/^\d+% probability\s*/i, '')}</span>
                  </>
                ) : (
                  <span className="text-foreground/90">{row.label}</span>
                )}
              </li>
            )) : (
              <li className="text-muted-foreground">No predicted opportunities yet.</li>
            )}
            {likelihood > 0 ? (
              <li>
                <span className="font-bold text-emerald-600">{likelihood}% probability</span>
                <span className="text-muted-foreground"> of hitting daily revenue target by close.</span>
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </motion.section>
  )
}
